<template>
    <main
        class="component-editor"
        :class="[containerClasses, movementCursor]"
        :style="containerStyles"
    >
        <toolbar-component :screen="screen" />

        <div
            v-if="selectedComponent"
            class="component-editor__content"
            :style="[styleFontFamily, stylePreviewDevice]"
            @dragenter="onDragEnter"
            @dragleave="onDragLeave"
            @drop="onDrop"
        >
            <img v-if="previewDevice" class="component-editor__device" :src="require(`@/assets/preview/${ deviceMode.deviceImage }`)">

            <div ref="content" class="component-editor__content-container" :style="stylePreviewContent">
                <component
                    :is="selectedComponent.elementType"
                    ref="component"
                    :top-level="true"
                    v-bind="selectedComponent"
                    @element-updated="updateRendering"
                />
            </div>
        </div>

        <div v-if="!selectedComponent && !loading" class="component-editor__empty">
            <h2 class="component-editor__empty-title">
                NO COMPONENT SELECTED
            </h2>

            <h3 class="component-editor__empty-subtitle">
                Please select a component from the menu on the left.
            </h3>
        </div>

        <div v-show="loading" class="component-editor__loading">
            <loading-component mode="light" />
        </div>

        <toolbar-editor key="toolbar-components" :screen="screen" />
        <snackbar />
    </main>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import LoadingComponent from '@/components/shared/loading/loading-component';
import ToolbarComponent from '@/components/component-editor/toolbar-component';
import ToolbarEditor from '@/components/shared/toolbars/toolbar-editor';

import { CREATE_COMPONENT, LOAD_PRESETS, LOAD_SETTINGS } from '@/store/types/action-types';
import { COMPONENT_TREE_ITEM_NAMES } from '@/store/types/getter-types';
import {
    CLEAR_SELECTED_ELEMENTS,
    SHOW_PROMPT,
    SET_CHANGED_DATA,
    SET_COMPONENT_TREE,
    SET_DRAGGING_COMPONENT,
    SET_LAST_COMPONENT_ID,
    SET_SELECTED_COMPONENT,
    SET_SELECTED_COMPONENT_METADATA,
    SET_SELECTED_ELEMENT,
    TOGGLE_ADDING_COMPONENT,
    TOGGLE_TOOLBAR,
} from '@/store/types/mutation-types';
import { tailwindConfig } from '@/data/tailwind';
import { SCREEN_NAMES } from '@/data/constants/screen';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';
import { fetchRendering, generateRendering } from '@/utils/rendering';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { STATE_CLASSES } from '@/data/constants/classes';
import * as settingsDataJson from '@/data/settings.json';

export default {
    name: 'component-editor',

    components: {
        LoadingComponent,
        ToolbarComponent,
        ToolbarEditor,
    },

    mixins: [listenOnRootMixin],
    inheritAttrs: false,

    props: {
        componentId: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            dragEventCount: 0,
            dynamicDataModalOpen: false,
            loading: true,
            screen: SCREEN_NAMES.COMPONENT_EDITOR,
            toolbars: {},
        };
    },

    computed: {
        ...mapGetters({
            componentTreeItemNames: COMPONENT_TREE_ITEM_NAMES,
        }),

        ...mapState({
            changedData: state => state.page.changedData,
            componentList: state => state.page.componentList,
            componentTree: state => state.page.componentTree,
            copiedElements: state => state.page.copiedElements,
            deviceMode: state => state.page.deviceMode,
            modal: state => state.page.modal,
            previewMode: state => state.previewMode,
            selectedComponent: state => state.page.selectedComponent,
            toolbarVisibility: state => state.page.toolbarVisibility,
        }),

        containerClasses() {
            return {
                [STATE_CLASSES.PREVIEW]: this.previewMode,
                [STATE_CLASSES.EMPTY]: !this.selectedComponent,
            };
        },

        containerStyles() {
            if (this.previewMode || !this.hasToolbars) {
                return {
                    'padding-left': '0px',
                    'padding-right': '0px',
                };
            }

            const { component, editor } = this.toolbars;

            return {
                'padding-left': this.toolbarVisibility.component
                    ? `${ component.width + component.borderWidth + (component.handleWidth * 2) }px`
                    : `${ component.borderWidth + component.collapseOffset + (component.handleWidth * 2) }px`,

                'padding-right': this.toolbarVisibility.editor
                    ? `${ editor.width + editor.borderWidth + (editor.handleWidth * 2) }px`
                    : `${ editor.borderWidth + editor.collapseOffset + (editor.handleWidth * 2) }px`,
            };
        },

        movementCursor() {
            if (!this.copiedElements.elements.length) {
                return '';
            }

            if (this.copiedElements.mode === 'cut') {
                return STATE_CLASSES.CUTTING;
            }

            if (this.copiedElements.mode === 'copy') {
                return STATE_CLASSES.COPYING;
            }

            return '';
        },

        hasToolbars() {
            return this.toolbars.component && this.toolbars.editor;
        },

        previewDevice() {
            return this.previewMode && (this.deviceMode.name === 'mob' || this.deviceMode.name === 'tab');
        },

        styleFontFamily() {
            return { fontFamily: tailwindConfig.theme.fontFamily.default };
        },

        stylePreviewDevice() {
            return {
                maxWidth: this.deviceMode.deviceWidth,
                height: this.deviceMode.deviceHeight,
            };
        },

        stylePreviewContent() {
            return {
                maxWidth: this.deviceMode.deviceContentWidth,
                height: this.deviceMode.deviceContentHeight,
            };
        },
    },

    watch: {
        $route(to) {
            if (to.name === 'components') {
                this.loadDefaultComponent();
            }
        },

        modal() {
            this.dynamicDataModalOpen = this.modal && this.modal.componentTag === 'modal-dynamic-options';
        },
    },

    created() {
        this.loadSettings();
        this.loadPresets();

        this.listenOnRoot('flex::toolbar::update', this.cacheToolbarDimensions);
        this.listenOnRoot('flex::component::select', this.onMenuSelect);

        this.toggleToolbar({ id: 'component', isVisible: true });
        this.toggleToolbar({ id: 'editor', isVisible: false });

        this.loadDefaultComponent();
    },

    destroyed() {
        this.setSelectedComponent(null);
        this.setSelectedComponentMetadata(null);
        this.toggleAddingComponent(false);
    },

    methods: {
        ...mapActions({
            createComponent: CREATE_COMPONENT,
            loadPresets: LOAD_PRESETS,
            loadSettings: LOAD_SETTINGS,
        }),

        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            showPrompt: SHOW_PROMPT,
            setChangedData: SET_CHANGED_DATA,
            setComponentTree: SET_COMPONENT_TREE,
            setDraggingComponent: SET_DRAGGING_COMPONENT,
            setLastComponentId: SET_LAST_COMPONENT_ID,
            setSelectedComponent: SET_SELECTED_COMPONENT,
            setSelectedComponentMetadata: SET_SELECTED_COMPONENT_METADATA,
            setSelectedElement: SET_SELECTED_ELEMENT,
            toggleAddingComponent: TOGGLE_ADDING_COMPONENT,
            toggleToolbar: TOGGLE_TOOLBAR,
        }),

        cacheToolbarDimensions({ id, dimensions }) {
            if (!this.toolbars[id]) {
                this.$set(this.toolbars, id, {});
            }

            this.toolbars[id] = { ...dimensions };
        },

        onDragEnter() {
            this.dragEventCount++;
            this.setDraggingComponent(true);
        },

        onDragLeave() {
            this.dragEventCount--;

            if (this.dragEventCount <= 0) {
                this.setDraggingComponent(false);
            }
        },

        onDrop() {
            this.dragEventCount = 0;
        },

        onMenuSelect(menuItem) {
            // if the dynamic data modal is open, ignore any "flex::component::select" events
            // ... as those are being used by the aformentioned modal
            if (this.dynamicDataModalOpen) return;

            if (this.changedData) {
                this.showPrompt({
                    template: 'unsaved-changes',
                    onContinue: () => this.loadMenuItem(menuItem),
                });
            } else {
                this.loadMenuItem(menuItem);
            }
        },

        loadMenuItem(menuItem) {
            const componentItemID = menuItem.props && menuItem.props.componentItemID;

            this.clearSelectedElements();

            if (componentItemID) {
                this.loadCustomComponent(componentItemID);
            } else {
                this.loadStandardComponent(menuItem);
            }

            this.setChangedData(false);
        },

        loadDefaultComponent() {
            const { componentId } = this.$route.params;

            if (componentId) {
                this.loadCustomComponent(componentId);
            } else {
                this.loading = false;
            }
        },

        async loadStandardComponent(menuItemData) {
            const rendering = await generateRendering(menuItemData);

            if (!this.componentList.includes(menuItemData.name)) {
                console.warn(`Unable to locate the base component "${ menuItemData.name }".`);
                return;
            }

            this.setSelectedComponentMetadata({
                editorName: menuItemData.displayName,
                componentType: 'Standard Component',
            });

            this.setSelected(rendering, true);
            this.$router.push({ path: '/components' }).catch(() => {});

            // n.b. newly selected components take 3 ticks to finish rendering
            await this.$nextTick();
            await this.$nextTick();
            await this.$nextTick();

            const spacingClass = `f-mb-${ settingsDataJson.default.spacing.slice(0, -3) }`;
            this.$refs.component.flexClassCollection.margin.all.b = spacingClass;
        },

        async loadCustomComponent(componentItemID) {
            this.loading = true;

            const rendering = await fetchRendering(componentItemID);

            if (!rendering) {
                this.loading = false;
                return;
            }

            this.setSelectedComponentMetadata({
                editorName: rendering.editorName,
                id: rendering.id,
                // using fact that Global Content components have linkedcomponentID
                componentType: rendering.globalContent ? COMPONENT_TREE_SECTIONS.SHARED : COMPONENT_TREE_SECTIONS.CLIENT,
            });

            this.setSelected(rendering, false);
            this.setLastComponentId(componentItemID);
            this.loading = false;

            // do not use this.$router.push as it causes the entire app to rerender
            // ... which can cause issues with components that contain asynchronous code
            window.history.pushState({}, null, `/components/${ componentItemID }`);
        },

        async setSelected(rendering, isAdding) {
            this.setSelectedComponent(null);
            await this.$nextTick();
            this.setSelectedComponent(rendering);
            this.toggleAddingComponent(isAdding);
        },

        updateRendering(updatedRendering) {
            this.setSelectedComponent(updatedRendering);
        },
    },
};
</script>

<style lang="scss">
.component-editor {
    display: flex;
    justify-content: center;
    color: var(--artboard-color);
    background-color: var(--artboard-backgroundColor);
    min-height: 100vh;
    transition: padding 0.35s;
    box-shadow: $header-box-shadow;

    &.#{ $is-cutting } {
        cursor: move;
    }

    &.#{ $is-copying } {
        cursor: copy;
    }

    &.#{ $is-empty } {
        align-items: center;
    }

    .#{ $is-preview } & {
        width: 100%;
        display: flex;
        justify-content: center;
    }

    .#{ $is-preview }.#{ $is-desktop } &,
    .#{ $is-preview }.#{ $is-laptop } & {
        background-color: $site-background-color;
    }

    .#{ $is-preview }.#{ $is-tablet } &,
    .#{ $is-preview }.#{ $is-mobile } & {
        align-items: center;
        min-height: 100vh;
    }

    &__content {
        display: flex;
        justify-content: center;
        position: relative;
        transition: all 0.33s;
        margin-left: auto;
        margin-right: auto;
        z-index: 50;
        width: 100%;

        .#{ $is-editing } & {
            padding-top: 50px; // to prevent the toolbars from overlapping with the blue header bar
        }

        .#{ $is-preview } & {
            align-items: center;
        }
    }

    &__content-container {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        width: 100%;

        .#{ $is-preview } & {
            background-color: $page-background-color;
            max-width: $max-site-width;
            height: 100%;
        }

        .#{ $is-preview }.#{ $is-tablet } &,
        .#{ $is-preview }.#{ $is-mobile } & {
            position: absolute;
            overflow: auto;
            top: 50%;
            transform: translateY(-50%);
        }

        .#{ $is-preview }.#{ $is-tablet } & {
            margin-top: -3px;
        }

        .#{ $is-preview }.#{ $is-mobile } & {
            margin-left: 1px;
        }
    }

    &__empty {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        h1, h2, h3, h4, h5, h6 {
            font-family: $default-font-family;
        }
    }

    &__empty-title {
        font-size: 30px;
        font-weight: 700;
    }

    &__empty-subtitle {
        margin-top: 7px;
        opacity: 0.75;
    }

    &__loading {
        @extend %fill;
        z-index: 1;
    }
}
</style>
